// What makes this app riviera-todo.com rather than another *-todo site: origin,
// brand, languages, timezone, currency, cities, sections and partner ids. Code
// that would be the same on every site reads these from here instead of
// hardcoding them, so it can move to a shared package (see
// docs/monorepo-plan.md, stage 2) — @todo/core reads it as the `@site` alias
// (tsconfig.json). Dependency-free: imported from anywhere.

import type { SiteConfig } from '@todo/core/site-config';

export const SITE = {
  // Short machine name (the Worker's): prefixes this site's browser storage keys.
  id: 'riviera-todo',

  // Absolute origin baked into canonical / og:url tags. Must be absolute for
  // crawlers. Override per-environment with EXPO_PUBLIC_SITE_URL if needed.
  url: (process.env.EXPO_PUBLIC_SITE_URL ?? 'https://riviera-todo.com').replace(/\/+$/, ''),
  name: 'Riviera To-Do',

  // UI languages, in switcher order. Each is a URL prefix (/en, /fr, /ru).
  locales: ['en', 'fr', 'ru'],
  defaultLocale: 'en',

  // Where the events happen: "today", "this weekend" and every rendered date
  // are days in this timezone, whatever the visitor's device says.
  timeZone: 'Europe/Paris',
  // The feeds' currency: prices are compared and filtered in it, and it's the
  // fallback for a row without one.
  currency: 'EUR',

  // `cities.id` values the site covers (the Supabase seed), in display order.
  cityIds: ['nice', 'cannes', 'monaco', 'antibes', 'sttropez', 'menton', 'villefranche'],

  // Top-level sections, in header order. Each is a /[lang]/<section> route.
  sections: ['events', 'tours', 'wineTours', 'boats', 'hotels', 'articles', 'cities'],

  // ISO 3166 country of the audience and the suppliers' billing region
  // (structured data: the zero-rate shipping / no-returns declarations).
  country: 'FR',
  // Event JSON-LD: every city is in Provence-Alpes-Côte d'Azur but Monaco, a
  // sovereign state (seo/event-jsonld.ts).
  eventAddress: {
    region: "Provence-Alpes-Côte d'Azur",
    countryByCity: { monaco: 'MC' },
  },

  // The destination, per locale: Organization.areaServed, and (in the default
  // locale) the region every city page's Place is contained in.
  regionName: {
    en: 'French Riviera',
    fr: "Côte d'Azur",
    ru: 'Лазурный Берег',
  },
  // Organization.description, per locale.
  description: {
    en: 'Things to do on the French Riviera — events, tours, wine tastings and boat rentals in Nice, Cannes, Monaco, Antibes, Saint-Tropez, Menton and Villefranche-sur-Mer.',
    fr: "Que faire sur la Côte d'Azur — agenda, excursions, dégustations de vin et location de bateaux à Nice, Cannes, Monaco, Antibes, Saint-Tropez, Menton et Villefranche-sur-Mer.",
    ru: 'Чем заняться на Лазурном Берегу — афиша, экскурсии, винные туры и аренда лодок в Ницце, Каннах, Монако, Антибе, Сен-Тропе, Ментоне и Вильфранш-сюр-Мер.',
  },
  // Breadcrumb labels for the JSON-LD built outside React (no t() there):
  // keep in sync with `nav.*` in src/lib/i18n/resources/*.json.
  crumbs: {
    tours: { en: 'Tours', fr: 'Excursions', ru: 'Экскурсии' },
    boats: { en: 'Yachts & Boats', fr: 'Yachts et bateaux', ru: 'Яхты и лодки' },
    wineTours: { en: 'Wine tours', fr: 'Excursions œnologiques', ru: 'Винные туры' },
    articles: { en: 'Articles', fr: 'Articles', ru: 'Статьи' },
  },

  stay22: {
    /** Stay22 affiliate id — public, it's in every link. */
    aid: 'rivieratodo',
    /** Campaign suffix for searches covering the whole destination, not one city. */
    region: 'riviera',
    /** The site's gold (Colors.light.gold). */
    mapColor: 'c7a35c',
    // The `cities.centroid` points from the seed migration, for pages that only
    // know the city (articles, city guides) — and events without a venue point.
    centroids: {
      nice: { lat: 43.7102, lng: 7.262 },
      cannes: { lat: 43.5528, lng: 7.0174 },
      monaco: { lat: 43.7384, lng: 7.4246 },
      antibes: { lat: 43.5808, lng: 7.125 },
      sttropez: { lat: 43.2727, lng: 6.6407 },
      menton: { lat: 43.7749, lng: 7.5 },
      villefranche: { lat: 43.7042, lng: 7.3111 },
    },
  },
} as const satisfies SiteConfig;

export type SiteLocale = (typeof SITE.locales)[number];
export type SiteCityId = (typeof SITE.cityIds)[number];
export type SiteSection = (typeof SITE.sections)[number];
